import BaseModal from "../layout/BaseModal"
import { ReactComponent as FOLDER } from "../../assets/Folder.svg"
import { ReactComponent as FILE } from "../../assets/Document.svg"
import { ReactComponent as DOWNLOAD } from "../../assets/DownloadICON.svg"
import { ReactComponent as DELETE } from "../../assets/DeleteICON.svg"
import BaseDivider from "./BaseDivider"
import BaseInputField from "./BaseInputField"
import { useMemo, useState } from "react"
import { useAddDocumentToFolderMutation, useAddFolderMutation, useDeleteDocMutation, useDeleteFolderMutation, useDownloadDocumentMutation } from "../../pages/api/services"
import { useQueryClient } from "@tanstack/react-query"
import { findElementsWithExactCharCount } from "../../services/functions"
import Dropzone from "react-dropzone"
import { DocumentDataType } from "../types"
import BasePopover from "./BasePopover"
import { Eye } from "lucide-react"
import BasePreview from "./BasePreview"
import { useContext } from "react"
import { SidePanelContext } from "../contexts/SidePanelContext"

type BaseFolderStructurModalProps = {
    modalId: number
    title: string
    id: string
    type: "customer" | "object" | "employee"
    data: string[]
    queryKey: string
    documents: DocumentDataType[]
    handlePreviewDocument: (s3Key: string) => void
}

export default function BaseFolderStructurModal(props: BaseFolderStructurModalProps) {
    // add folder
    const queryClient = useQueryClient()
    const { mutate: addFolder } = useAddFolderMutation(queryClient, props.queryKey)
    const [folderName, setfolderName] = useState<string>()
    const handleAddFolder = () => {
        if (folderName && folderName !== "") {
            addFolder({ collectionType: props.type, id: props.id, folder: `${treeRoot}${folderName}`.replace("/", "") })
            setfolderName(undefined)
        }
    }

    // delete folder
    const { mutate: deleteFolder } = useDeleteFolderMutation(queryClient, props.queryKey)
    const handleDeleteFolder = (folder: string) => {
        deleteFolder({ collectionType: props.type, id: props.id, folder: folder })
    }

    // tree root structure
    const [treeRoot, setTreeRoot] = useState<string>("")
    const [formerTreeRoot, setFormerTreeRoot] = useState<string[]>([])
    const folderTree = useMemo(() => findElementsWithExactCharCount(props.data, treeRoot), [props.data, treeRoot])

    // logic to navigate in folder tree
    const handleFolderNavigate = (folder: string) => {
        setFormerTreeRoot([...formerTreeRoot, treeRoot])
        setTreeRoot(`${folder}`)
    }

    // logic to navigate back in folder tree
    const handleBack = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        if (formerTreeRoot.length > 0) {
            setTreeRoot(formerTreeRoot[formerTreeRoot.length - 1])
            setFormerTreeRoot(formerTreeRoot.slice(0, -1))
        }
    }
    // add document to folder
    const { mutate: addDocument } = useAddDocumentToFolderMutation(queryClient, props.queryKey)
    const handleFileUpload = (documents: any[]) => {
        const formData = new FormData()
        let processedDocuments = documents.map((doc, index) => {
            return {
                title: "",
                fileUpload: doc,
                uploadedByUser: true,
            }
        })
        processedDocuments.forEach((doc, index) => {
            if (doc && doc.fileUpload instanceof File) {
                formData.append("files", doc.fileUpload)
            }
        })
        addDocument({
            id: props.id,
            data: formData,
            path: encodeURIComponent(treeRoot),
            type: props.type,
        })
    }

    // download file from object
    const { mutate: downloadFile } = useDownloadDocumentMutation()
    const handleDownload = (s3Key: string, filename: string) => {
        downloadFile({ id: props.id, s3Key: s3Key, filetype: props.type, filename: filename })
    }

    // delete file
    const { mutate: deleteDoc } = useDeleteDocMutation(queryClient, props.queryKey)
    const handleDeleteDoc = (file: string) => {
        deleteDoc({ id: props.id, file: file, type: props.type })
    }

    return (
        <BaseModal
            modalId={props.modalId}
            title={"Ordner - " + props.title}
            bottomRow={
                <div className="flex flex-row gap-2 w-full justify-end">
                    {folderName !== undefined && (
                        <button className="text-xs hover:underline w-fit" onClick={() => setfolderName(undefined)}>
                            Abbrechen
                        </button>
                    )}
                    {folderName !== undefined && <BaseInputField label={""} type={"text"} onChange={setfolderName} />}
                    <button
                        className="text-xs hover:underline w-40"
                        onClick={() => {
                            if (folderName === undefined) {
                                setfolderName("")
                            } else {
                                handleAddFolder()
                            }
                        }}>
                        {folderName === undefined ? "Neuer Ordner" : "+ Ordner hinzufügen"}
                    </button>
                </div>
            }>
            <Dropzone onDrop={(acceptedFiles) => handleFileUpload(acceptedFiles)}>
                {({ getRootProps, getInputProps }) => (
                    <div className="flex flex-col h-full cursor-pointer" {...getRootProps()}>
                        <div className="flex flex-row gap-4 w-full justify-start py-2">
                            <button className="text-xs hover:underline" onClick={(event) => handleBack(event)}>
                                Zurück
                            </button>
                            <p className="text-xs text-gray-300 w-48">{treeRoot === "" ? "/" : treeRoot}</p>
                        </div>
                        <div>
                            {folderTree?.map((item, index) => (
                                <>
                                    <FolderRow
                                        key={index}
                                        name={treeRoot === "" ? item.split("/")[1] : item.replace(treeRoot, "").replace("/", "")}
                                        setTreeRoot={() => handleFolderNavigate(item)}
                                        handleDeleteFolder={() => handleDeleteFolder(item)}
                                    />
                                    <BaseDivider light classProps="" />
                                </>
                            ))}
                            {props.documents?.map(
                                (item, index) =>
                                    item.folder === (treeRoot === "" ? "/" : treeRoot) && (
                                        <>
                                            <DocumentRow
                                                key={index}
                                                item={item}
                                                handleDeleteDocument={() => handleDeleteDoc(item.s3Key)}
                                                handleDownloadDocument={() => handleDownload(item.s3Key, item.title)}
                                                handlePreviewDocument={(e: string) => props.handlePreviewDocument(e)}
                                            />
                                            <BaseDivider light classProps="" />
                                        </>
                                    )
                            )}
                            <p className="text-xs text-gray-300 text-center mt-2">Für Dokumentupload klicken oder hier ablegen</p>
                            <input {...getInputProps()} />
                        </div>
                    </div>
                )}
            </Dropzone>
        </BaseModal>
    )
}

type FolderRowProps = {
    name: string
    setTreeRoot: () => void
    handleDeleteFolder: () => void
}

function FolderRow(props: FolderRowProps) {
    return (
        <div
            className="flex flex-row justify-between gap-2 w-full hover:bg-gray-100 p-2 cursor-pointer text-sm items-center -z-10"
            onClick={(event) => {
                event.stopPropagation()
                props.setTreeRoot()
            }}>
            <div className="flex flex-row gap-2 items-center">
                <FOLDER className="w-5 h-5" />
                <p>{props.name}</p>
            </div>
            <BasePopover
                label={"Ordner löschen"}
                okButtonClick={(event) => {
                    event.stopPropagation()
                    props.handleDeleteFolder()
                }}
                children={<DELETE className="w-4 h-4 z-10 hover:font-semibold" />}
            />
        </div>
    )
}

type DocumentRowProps = {
    item: DocumentDataType
    handleDeleteDocument: () => void
    handleDownloadDocument: () => void
    handlePreviewDocument: (e: string) => void
}

function DocumentRow(props: DocumentRowProps) {
    return (
        <div className="flex flex-row justify-between gap-2 w-full hover:bg-gray-100 p-2 cursor-pointer text-sm items-center -z-10">
            <div className="flex flex-row gap-2 items-center text-xs">
                <FILE className="w-4 h-4" />
                <p>{props.item.title}</p>
            </div>
            <div className="flex flex-row gap-6 items-center">
                {props.item.title?.endsWith(".pdf") && (
                    <Eye
                        onClick={(event) => {
                            event.stopPropagation()
                            props.handlePreviewDocument(props.item.s3Key)
                        }}
                        className="w-4 h-4 z-10 hover:font-semibold cursor-pointer"
                    />
                )}
                <DOWNLOAD
                    onClick={(event) => {
                        event.stopPropagation()
                        props.handleDownloadDocument()
                    }}
                    className="w-4 h-4 z-10 hover:font-semibold"
                />
                <BasePopover
                    label={"Dokument löschen"}
                    okButtonClick={(event) => {
                        event.stopPropagation()
                        props.handleDeleteDocument()
                    }}
                    children={<DELETE className="w-4 h-4 z-10 hover:font-semibold" />}
                />
            </div>
        </div>
    )
}
